export const projectsData = {
  1: {
    id: 1,
    title: 'Real-Time Operating System Kernel',
    description: 'Custom RTOS kernel built from scratch in C/Assembly with preemptive scheduling, memory management, and hardware abstraction layer. Features include task synchronization, interrupt handling, and device drivers for ARM Cortex-M microcontrollers.',
    longDescription: `A comprehensive real-time operating system kernel designed for embedded applications requiring deterministic behavior and minimal latency. This project demonstrates deep understanding of low-level system programming, hardware interfaces, and real-time constraints.

The kernel implements a preemptive scheduler with priority-based task management, ensuring critical tasks meet their deadlines. Memory management includes both static and dynamic allocation strategies optimized for embedded environments with limited resources.

Key architectural decisions include a microkernel design for modularity, hardware abstraction layers for portability across different ARM Cortex-M variants, and efficient interrupt handling mechanisms that minimize system overhead while maintaining real-time guarantees.`,
    tech: ['C', 'Assembly', 'ARM Cortex-M', 'RTOS', 'Hardware Drivers'],
    features: [
      'Preemptive multitasking with priority scheduling',
      'Memory management with heap and stack protection',
      'Hardware abstraction layer for multiple MCU families',
      'Interrupt-driven I/O with minimal latency',
      'Task synchronization primitives (mutexes, semaphores)',
      'Real-time clock and timer management',
      'Device driver framework',
      'Power management and sleep modes'
    ],
    challenges: [
      'Implementing deterministic scheduling algorithms',
      'Optimizing context switching overhead',
      'Managing memory constraints in embedded environments',
      'Ensuring interrupt safety and real-time guarantees',
      'Creating portable hardware abstraction layers'
    ],
    results: [
      'Sub-microsecond interrupt response times',
      'Support for 50+ concurrent tasks',
      'Memory footprint under 32KB',
      'Portable across 15+ ARM Cortex-M variants',
      'Used in production IoT devices'
    ],
    github: 'https://github.com/username/rtos-kernel',
    demo: 'https://demo.rtos-kernel.com',
    documentation: 'https://docs.rtos-kernel.com',
    featured: true,
    status: 'Production',
    startDate: '2023-06-01',
    endDate: '2024-01-15',
    teamSize: 1,
    codeLines: 15000,
    image: 'https://readdy.ai/api/search-image?query=Technical%20diagram%20of%20real-time%20operating%20system%20architecture%20with%20task%20scheduler%2C%20memory%20management%2C%20and%20hardware%20abstraction%20layers%2C%20hand-drawn%20style%20on%20whiteboard%20with%20black%20markers%2C%20minimalist%20technical%20illustration&width=800&height=400&seq=101&orientation=landscape'
  },
  2: {
    id: 2,
    title: 'High-Performance HTTP Server',
    description: 'Multi-threaded web server implemented in C++ with custom memory pool allocator, epoll-based I/O multiplexing, and zero-copy optimizations. Handles 10k+ concurrent connections with minimal latency.',
    longDescription: `A high-performance HTTP/1.1 and HTTP/2 server built from the ground up in modern C++. This project showcases advanced systems programming techniques including custom memory management, efficient I/O multiplexing, and zero-copy optimizations.

The server architecture employs an event-driven design using Linux epoll for scalable connection handling. Custom memory pool allocators eliminate allocation overhead during request processing, while zero-copy techniques minimize data movement between kernel and user space.

Performance optimizations include connection pooling, HTTP keep-alive support, efficient header parsing, and SIMD-accelerated string operations. The server maintains sub-millisecond response times even under heavy load conditions.`,
    tech: ['C++', 'Linux', 'Epoll', 'Threading', 'Memory Optimization'],
    features: [
      'HTTP/1.1 and HTTP/2 protocol support',
      'Epoll-based event-driven architecture',
      'Custom memory pool allocators',
      'Zero-copy file serving',
      'Thread pool for request processing',
      'Connection keep-alive and pipelining',
      'GZIP compression support',
      'SSL/TLS encryption',
      'Virtual host support',
      'Access logging and metrics'
    ],
    challenges: [
      'Implementing efficient HTTP/2 multiplexing',
      'Optimizing memory allocation patterns',
      'Handling edge cases in HTTP parsing',
      'Balancing thread pool sizing',
      'Implementing zero-copy file transfers'
    ],
    results: [
      '10,000+ concurrent connections',
      'Sub-millisecond average response time',
      '50% lower memory usage than nginx',
      '99.9% uptime in production',
      'Handles 100k requests/second'
    ],
    github: 'https://github.com/username/http-server',
    demo: 'https://demo.http-server.com',
    documentation: 'https://docs.http-server.com',
    featured: true,
    status: 'Production',
    startDate: '2023-03-01',
    endDate: '2023-11-30',
    teamSize: 1,
    codeLines: 12000,
    image: 'https://readdy.ai/api/search-image?query=Network%20architecture%20diagram%20showing%20HTTP%20server%20with%20multiple%20client%20connections%2C%20load%20balancing%2C%20and%20data%20flow%20arrows%2C%20hand-drawn%20whiteboard%20style%20with%20black%20markers%2C%20technical%20illustration&width=800&height=400&seq=102&orientation=landscape'
  },
  3: {
    id: 3,
    title: 'Containerized Microservices Platform',
    description: 'Complete microservices architecture using Docker containers with service discovery, load balancing, and automated deployment pipelines. Built with React frontend and C++ backend services.',
    longDescription: `A comprehensive microservices platform demonstrating modern cloud-native architecture patterns. The system combines containerized C++ backend services with a responsive React frontend, showcasing full-stack development expertise.

The platform implements service mesh architecture with automatic service discovery, health checking, and circuit breaker patterns. Docker containers provide consistent deployment environments, while Kubernetes orchestration ensures high availability and scalability.

CI/CD pipelines automate testing, building, and deployment processes. The system includes comprehensive monitoring, logging, and alerting capabilities for production operations.`,
    tech: ['Docker', 'C++', 'React', 'Kubernetes', 'CI/CD'],
    features: [
      'Microservices architecture with 12+ services',
      'Docker containerization for all components',
      'Kubernetes orchestration and scaling',
      'Service mesh with Istio',
      'React frontend with TypeScript',
      'C++ backend services with gRPC',
      'PostgreSQL and Redis data stores',
      'Prometheus monitoring and Grafana dashboards',
      'ELK stack for centralized logging',
      'GitLab CI/CD pipelines'
    ],
    challenges: [
      'Designing service boundaries and APIs',
      'Implementing distributed tracing',
      'Managing inter-service communication',
      'Handling data consistency across services',
      'Optimizing container resource usage'
    ],
    results: [
      '99.95% system availability',
      'Auto-scaling from 5 to 100+ instances',
      'Sub-second deployment times',
      '50% reduction in infrastructure costs',
      'Zero-downtime deployments'
    ],
    github: 'https://github.com/username/microservices-platform',
    demo: 'https://demo.microservices-platform.com',
    documentation: 'https://docs.microservices-platform.com',
    featured: false,
    status: 'Production',
    startDate: '2023-01-01',
    endDate: '2023-08-31',
    teamSize: 3,
    codeLines: 25000,
    image: 'https://readdy.ai/api/search-image?query=Microservices%20architecture%20diagram%20with%20Docker%20containers%2C%20service%20mesh%2C%20and%20data%20flow%20between%20components%2C%20hand-drawn%20whiteboard%20style%20with%20black%20markers%20and%20arrows%2C%20technical%20system%20design&width=800&height=400&seq=103&orientation=landscape'
  }
};